import type { FormInstance } from 'antd';
import { createStore } from 'zustand';
import { combine } from 'zustand/middleware';

import type { BulkEditInitialValues } from 'app/typings/bulkEdit';
import type { ExtranetExperience, ExtranetPackage } from 'app/typings/packages';
import { formatDateWithoutHour } from 'app/utils/dates';
import type { Interval } from 'app/utils/dayjs';
import { addDays, eachDayOfInterval } from 'app/utils/dayjs';

import type {
  DuplicateType,
  DuplicationModeData,
  ErrorsListType,
  FormInventorySaved,
  FormType,
  TimePeriod,
} from './type';
import { DEFAULT_COLLAPSE_OVERVIEW_KEY } from './type';
import { getDatesToFill, handleFieldsChange } from './utils';

const INVENTORY_DATE_SIZE = 16;

type SaveChangesModalType = {
  isVisible: boolean;
  timePeriod: TimePeriod;
};

type InventoryStoreValuesType = {
  autoInventoryModalOpen: boolean;
  pricingRuleModalOpen: boolean;
  form?: FormInstance<FormType>;
  isBulkEditOpen: boolean;
  isEditMode: boolean;
  openEditMaxBooking: boolean;
  timePeriod: TimePeriod;
  formTimePeriod: TimePeriod;
  formHasChanged: boolean;
  inventoryErrors: ErrorsListType[];
  inventorySaved: FormInventorySaved;
  selectPackage: number[];
  inventory: FormType | undefined;
  packages: ExtranetPackage[];
  currentDuplicateHoverRange?: Interval;
  bookableDays: string[];
  activeCollapseKey: string | string[];
  saveChangesModal: SaveChangesModalType;
  bulkEditInitialValues: BulkEditInitialValues;
};

type InventoryStoreMutationType = {
  setForm: (form: FormInstance<FormType>) => void;
  setIsEditMode: (value: boolean) => void;
  setAutoInventoryModalOpen: (value: boolean) => void;
  setPricingRuleModalOpen: (value: boolean) => void;
  setOpenEditMaxBooking: (openEditMaxBooking: boolean) => void;
  setIsBulkEditOpen: (value: boolean) => void;
  setTimePeriod: (period: TimePeriod) => void;
  setFormHasChanged: (formHasChanged: boolean) => void;
  setInventoryErrors: (inventoryErrors: ErrorsListType[]) => void;
  setInventorySaved: (inventorySaved: FormInventorySaved) => void;
  setSelectPackage: (selectPackage: number[]) => void;
  setActiveCollapseKey: (activeKey: string | string[]) => void;
  setSaveChangesModal: (saveChangesModal: SaveChangesModalType) => void;
  setInventory: (inventory?: FormType) => void;
  updateInventory: (timePeriod: TimePeriod, inventory: FormType) => void;
  handleDuplicateHover: (day: Date, hoverMode: DuplicationModeData) => void;
  dropDuplicationHover: () => void;
  handleDuplicate: (
    duplicateData: DuplicateType,
    packages: ExtranetPackage[],
    getExperiencesForPackage: (packageId: number) => ExtranetExperience[],
    roomCount: number,
    hasAtLeastOneDayPackage: boolean
  ) => void;
  cancelForm: () => void;
  setBookableDays: (bookableDays: string[]) => void;
  setBulkEditInitialValues: (initialValues: BulkEditInitialValues) => void;
  resetInventory: () => void;
};

export const initialValues: InventoryStoreValuesType = {
  autoInventoryModalOpen: false,
  pricingRuleModalOpen: false,
  isBulkEditOpen: false,
  isEditMode: false,
  openEditMaxBooking: false,
  timePeriod: {
    startDate: new Date(),
    endDate: addDays(new Date(), INVENTORY_DATE_SIZE - 1),
  },
  formTimePeriod: {
    startDate: new Date(),
    endDate: addDays(new Date(), INVENTORY_DATE_SIZE - 1),
  },
  formHasChanged: false,
  inventoryErrors: [],
  inventorySaved: { packages: [], rooms: [] },
  selectPackage: [],
  inventory: undefined,
  packages: [],
  currentDuplicateHoverRange: undefined,
  bookableDays: [],
  activeCollapseKey: [DEFAULT_COLLAPSE_OVERVIEW_KEY],
  saveChangesModal: {
    isVisible: false,
    timePeriod: {
      startDate: new Date(),
      endDate: addDays(new Date(), INVENTORY_DATE_SIZE - 1),
    },
  },
  bulkEditInitialValues: {},
};

export const inventoryStore = createStore(
  combine<InventoryStoreValuesType, InventoryStoreMutationType>(
    initialValues,
    (set, get) => ({
      setForm: (form: FormInstance) => set({ form }),
      setIsEditMode: (isEditMode: boolean) => set({ isEditMode }),
      setAutoInventoryModalOpen: (autoInventoryModalOpen: boolean) =>
        set({ autoInventoryModalOpen }),
      setPricingRuleModalOpen: (pricingRuleModalOpen: boolean) =>
        set({ pricingRuleModalOpen }),
      setOpenEditMaxBooking: (openEditMaxBooking: boolean) =>
        set({ openEditMaxBooking }),
      setIsBulkEditOpen: (isBulkEditOpen: boolean) => set({ isBulkEditOpen }),
      setTimePeriod: (timePeriod: TimePeriod) => set({ timePeriod }),
      setFormHasChanged: (formHasChanged: boolean) => set({ formHasChanged }),
      setInventoryErrors: (inventoryErrors: ErrorsListType[]) =>
        set({ inventoryErrors }),
      setInventorySaved: (inventorySaved: FormInventorySaved) =>
        set({ inventorySaved }),
      setSelectPackage: (selectPackage: number[]) => set({ selectPackage }),
      setActiveCollapseKey: (activeKey: string | string[]) =>
        set({ activeCollapseKey: activeKey }),
      setSaveChangesModal: (saveChangesModal: SaveChangesModalType) =>
        set({ saveChangesModal }),
      setInventory: (inventory?: FormType) => set({ inventory }),
      handleDuplicateHover: (day: Date, hoverMode: DuplicationModeData) => {
        const range = getDatesToFill(day, hoverMode, get().timePeriod);
        const rangeWithoutCurrentDay: Interval = {
          start: addDays(range.start, 1),
          end: range.end,
        };

        set({ currentDuplicateHoverRange: rangeWithoutCurrentDay });
      },
      dropDuplicationHover: () =>
        set({ currentDuplicateHoverRange: undefined }),
      handleDuplicate: (
        duplicateData: DuplicateType,
        packages: ExtranetPackage[],
        getExperiencesForPackage: (packageId: number) => ExtranetExperience[],
        roomCount: number,
        hasAtLeastOneDayPackage: boolean
      ) => {
        const { day, modeData, roomId, packageId, value, property } =
          duplicateData;
        const { form, inventory, timePeriod } = get();

        if (!inventory || !form) {
          return;
        }

        const datesToFill = getDatesToFill(day, modeData, timePeriod);

        const packageName = packageId
          ? ['packages', packageId, 'bookings']
          : ['packages'];

        const name = roomId ? ['rooms', roomId, 'plannings'] : packageName;

        eachDayOfInterval(datesToFill).forEach((date) => {
          const formattedDate = formatDateWithoutHour(date);
          const basePath = [...name, formattedDate];
          const fullPath =
            property === 'stock' ||
            property === 'bar' ||
            property === 'maxBooking' ||
            packageId === undefined
              ? [...basePath, property]
              : [...basePath, 'packagePrices', packageId?.toString(), property];

          form.setFieldValue(fullPath, value);

          handleFieldsChange(
            [{ name: fullPath, value }],
            (formHasChanged: boolean) => set({ formHasChanged }),
            packages,
            form,
            getExperiencesForPackage,
            roomCount,
            hasAtLeastOneDayPackage
          );
        });
      },
      cancelForm: () => {
        const { form } = get();

        form?.resetFields();
        set({ formHasChanged: false, isEditMode: false });
      },
      updateInventory: (timePeriod: TimePeriod, inventory: FormType) => {
        const { form } = get();
        const roomIds =
          Object.values(inventory.rooms).map((r) => r.roomId.toString()) ?? [];
        const roomToExpand = roomIds.length > 0 ? roomIds : [];

        form?.setFieldsValue(inventory);
        set({
          activeCollapseKey: [...roomToExpand, DEFAULT_COLLAPSE_OVERVIEW_KEY],
          inventory,
          formTimePeriod: timePeriod,
        });
      },
      setBookableDays: (bookableDays) => set({ bookableDays }),
      setBulkEditInitialValues: (bulkEditInitialValues) =>
        set({ bulkEditInitialValues }),
      resetInventory: () => set(initialValues),
    })
  )
);
